import confetti from "canvas-confetti"
import PropTypes from "prop-types"
import React, { useEffect, useState } from "react"
import { Button, Form } from "react-bootstrap"
import api from "../api/api"
import { useAlert } from "../context/AlertContext"
import { inForgivenRange } from "../constants"
import SingleMediaPicker from "./SingleMediaPicker"

const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/gif"]
const ALLOWED_VIDEO_TYPES = ["video/mp4", "video/quicktime", "video/x-msvideo", "video/webm"]
const MAX_FILE_SIZE = 100 * 1024 * 1024

const CELEBRATION_SOUND_STORAGE_KEY =
	"plasticise_celebration_sound_enabled"

const successSound = new Audio("/success.mp3")
successSound.volume = 0.033

const isCelebrationSoundEnabled = () => {
	const storedPreference = localStorage.getItem(
		CELEBRATION_SOUND_STORAGE_KEY
	)

	if (storedPreference === null) {
		return true
	}

	return storedPreference === "true"
}

const ExerciseFeedback = ({
	exercise,
	onComplete,
	isCompleted,
	selectedDate,
}) => {
	const { showAlert } = useAlert()

	const [effort, setEffort] = useState(0)
	const [feeling, setFeeling] = useState("")
	const [mediaFile, setMediaFile] = useState(null)
	const [loading, setLoading] = useState(false)
	const [existingFeedbackId, setExistingFeedbackId] = useState(null)

	const [originalValues, setOriginalValues] = useState({
		effort: 0,
		feeling: "",
		mediaFile: null,
	})

	const [hasChanges, setHasChanges] = useState(false)

	useEffect(() => {
		const fetchExistingFeedback = async () => {
			try {
				const response = await api.get(
					`/feedback/?exercise=${exercise.id}&date_completed=${selectedDate}`
				)

				if (response.data && response.data.length > 0) {
					const latestFeedback = response.data[0]
					setExistingFeedbackId(latestFeedback.id)

					const effortValue = latestFeedback.effort_rating || 0
					const feelingValue = latestFeedback.feeling || ""
					let mediaFileValue = null

					if (latestFeedback.image_url || latestFeedback.video_url) {
						const mediaUrl = latestFeedback.image_url || latestFeedback.video_url
						const mediaKind = latestFeedback.video_url ? "video" : "image"
						const fullFilename = mediaUrl.split("/").pop()
						const extensions = [".jpg", ".jpeg", ".png", ".gif", ".mp4", ".avi", ".mov", ".webm"]
						let cutoffIndex = -1

						extensions.forEach((ext) => {
							const index = fullFilename.toLowerCase().lastIndexOf(ext)
							if (index !== -1 && index > cutoffIndex) {
								cutoffIndex = index + ext.length
							}
						})

						const filename =
							cutoffIndex !== -1
								? fullFilename.substring(0, cutoffIndex)
								: fullFilename

						mediaFileValue = {
							kind: mediaKind,
							file: null,
							name: filename,
							isExisting: true,
							url: mediaUrl,
						}
					}

					setEffort(effortValue)
					setFeeling(feelingValue)
					setMediaFile(mediaFileValue)

					setOriginalValues({
						effort: effortValue,
						feeling: feelingValue,
						mediaFile: mediaFileValue,
					})
				}
			} catch (err) {
				console.error("Error fetching existing feedback:", err)
			}
		}

		fetchExistingFeedback()
	}, [exercise.id, selectedDate])

	useEffect(() => {
		if (existingFeedbackId) {
			const hasEffortChanged = effort !== originalValues.effort
			const hasFeelingChanged = feeling !== originalValues.feeling
			const hasMediaChanged =
				(mediaFile?.kind || null) !== (originalValues.mediaFile?.kind || null) ||
				(mediaFile?.name || null) !== (originalValues.mediaFile?.name || null) ||
				(mediaFile?.isExisting || false) !== (originalValues.mediaFile?.isExisting || false)

			setHasChanges(hasEffortChanged || hasFeelingChanged || hasMediaChanged)
		}
	}, [effort, feeling, mediaFile, originalValues, existingFeedbackId])

	const validateFile = (media) => {
		if (!media || !media.file) return true

		const file = media.file
		const fileType = (file.type || "").split(";")[0].trim().toLowerCase()
		const isImage = ALLOWED_IMAGE_TYPES.includes(fileType)
		const isVideo = ALLOWED_VIDEO_TYPES.includes(fileType)

		if (!isImage && !isVideo) {
			showAlert("danger", "Please upload only images (JPEG, PNG, GIF) or videos (MP4, AVI, MOV, WebM)")
			return false
		}

		if (file.size > MAX_FILE_SIZE) {
			showAlert("danger", "File size must be less than 100MB")
			return false
		}

		return true
	}

	const playCompletionCelebration = () => {
		if (isCelebrationSoundEnabled()) {
			successSound.pause()
			successSound.currentTime = 0
			successSound.volume = 0.033
			successSound.play().catch((err) => {
				console.warn("Could not play success sound:", err)
			})
		}

		const prefersReducedMotion = window.matchMedia?.(
			"(prefers-reduced-motion: reduce)"
		).matches

		if (!prefersReducedMotion) {
			confetti({
				particleCount: 100,
				spread: 70,
				origin: { y: 0.6 },
			})
		}
	}

	const handleSubmit = async (e) => {
		e.preventDefault()
		setLoading(true)

		if (!validateFile(mediaFile)) {
			setLoading(false)
			return
		}

		const formData = new FormData()
		formData.append("exercise", exercise.id)
		formData.append("effort_rating", effort)
		formData.append("feeling", feeling)
		formData.append("date_completed", selectedDate)

		if (mediaFile?.file) {
			formData.append("media", mediaFile.file)
		}

		try {
			let feedbackResponse

			if (existingFeedbackId) {
				feedbackResponse = await api.put(
					`/feedback/${existingFeedbackId}/`,
					formData,
					{
						headers: {
							"Content-Type": "multipart/form-data",
						},
					}
				)

				showAlert("success", "Feedback updated successfully!")

				setOriginalValues({
					effort,
					feeling,
					mediaFile,
				})
				setHasChanges(false)
			} else {
				feedbackResponse = await api.post("/feedback/", formData, {
					headers: {
						"Content-Type": "multipart/form-data",
					},
				})

				showAlert("success", "Feedback submitted successfully!")
				playCompletionCelebration()
				onComplete(exercise.id)
			}

			if (feedbackResponse.status >= 400) {
				throw new Error(
					feedbackResponse.data?.message || "Failed to submit feedback"
				)
			}
		} catch (err) {
			let errorMessage = "An error occurred while submitting feedback"

			if (err.response?.status === 413) {
				errorMessage = "Video file size must be less than 100MB. Please try again with a smaller file."
			} else if (err.response?.data?.message) {
				errorMessage = err.response.data.message
			} else if (err.message) {
				errorMessage = err.message
			}

			showAlert("danger", errorMessage)
		} finally {
			setLoading(false)
		}
	}

	const getButtonText = () => {
		if (loading) return "Submitting..."
		if (existingFeedbackId) return "Update Feedback"
		if (isCompleted) return "Completed! ✓"
		return "I did it! 💪"
	}

	const effortOptions = [
		{ value: 0, label: "Not rated" },
		{ value: 1, label: "1 - Nothing" },
		{ value: 2, label: "2 - Very Easy" },
		{ value: 3, label: "3 - Easy" },
		{ value: 4, label: "4 - Comfortable" },
		{ value: 5, label: "5 - Somewhat difficult" },
		{ value: 6, label: "6 - Difficult" },
		{ value: 7, label: "7 - Hard" },
		{ value: 8, label: "8 - Very Hard" },
		{ value: 9, label: "9 - Extremely Hard" },
		{ value: 10, label: "10 - Maximal" },
	]

	return (
		<Form onSubmit={handleSubmit} className="mt-4 border-top pt-4">
			<Form.Group className="mb-4">
				<Form.Label>Rate your effort (Optional)</Form.Label>
				<Form.Select
					value={effort}
					onChange={(e) => setEffort(Number(e.target.value))}
					className="form-control-lg"
				>
					{effortOptions.map((option) => (
						<option key={option.value} value={option.value}>
							{option.label}
						</option>
					))}
				</Form.Select>
			</Form.Group>

			<Form.Group className="mb-4">
				<Form.Label>How did you feel? (Optional)</Form.Label>
				<Form.Control
					as="textarea"
					rows={3}
					placeholder="Share your experience..."
					value={feeling}
					onChange={(e) => setFeeling(e.target.value)}
				/>
			</Form.Group>

			<Form.Group className="mb-4">
				<SingleMediaPicker
					allowedKinds={["image", "video"]}
					value={mediaFile}
					onChange={(nextValue) => {
						setMediaFile(nextValue)
					}}
					onError={(msg) => showAlert("danger", msg)}
					maxSizeBytes={MAX_FILE_SIZE}
					maxVideoDurationSec={30}
					imageAccept={ALLOWED_IMAGE_TYPES}
					videoAccept={ALLOWED_VIDEO_TYPES}
					title="Share your practice, progress or get feedback (optional)"
					subtitle=""
					compact
				/>
			</Form.Group>

			<div className="d-grid">
				<Button
					className="py-3 mb-5 custom-pink-button"
					type="submit"
					variant="primary"
					size="lg"
					disabled={
						loading ||
						(!existingFeedbackId && !inForgivenRange(selectedDate)) ||
						(existingFeedbackId && !hasChanges)
					}
				>
					{getButtonText()}
				</Button>
			</div>
		</Form>
	)
}

ExerciseFeedback.propTypes = {
	exercise: PropTypes.shape({
		id: PropTypes.number.isRequired,
	}).isRequired,
	onComplete: PropTypes.func.isRequired,
	isCompleted: PropTypes.bool.isRequired,
	selectedDate: PropTypes.string.isRequired,
}

export default ExerciseFeedback